"use client"

import type React from "react"
import { useState, useEffect } from "react"
import InputField from "components/InputField"
import Button from "components/Button"
import Badge from "components/Badge"
import { Loader2, Plus, Building2, Trash2, Copy, Eye, EyeOff } from "lucide-react"
import { createLevel, getLevels } from "api/apiHelper"
import { toast } from "react-toastify"
import ErrorBox from "components/ErrorBox"
import { usePermissions } from "app/hooks"
import { useNavigate } from "react-router-dom"

interface LevelData {
  code: string
  order: number
  promotion_timeline: string
}

interface ExistingLevel {
  id: number
  code: string
  order: number
  promotion_timeline: string
  created_at?: string
}

interface CreateLevelFormProps {
  onSuccess?: () => void
  onError?: (error: string) => void
}

export function CreateLevelForm({ onSuccess, onError }: CreateLevelFormProps) {
  const [levels, setLevels] = useState<LevelData[]>([{ code: "", order: 1, promotion_timeline: "" }])
  const [existingLevels, setExistingLevels] = useState<ExistingLevel[]>([])
  const [showExistingLevels, setShowExistingLevels] = useState(true)
  const [loadingExisting, setLoadingExisting] = useState(false)
  const [errors, setErrors] = useState<{ [key: number]: Partial<LevelData> }>({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const permissions = usePermissions() || []
  const navigate = useNavigate();

  const fetchExistingLevels = async () => {
    try {
      setLoadingExisting(true)
      const response = await getLevels()
      setExistingLevels(response.data)
    } catch (err) {
      console.error("Error fetching existing levels:", err)
      setExistingLevels([])
    } finally {
      setLoadingExisting(false)
    }
  }

  useEffect(() => {
    fetchExistingLevels()
  }, [])

  const validateForm = (): boolean => {
    const newErrors: { [key: number]: Partial<LevelData> } = {}
    let hasErrors = false

    levels.forEach((level, index) => {
      const levelErrors: Partial<LevelData> = {}

      if (!level.code.trim()) {
        levelErrors.code = "Level code is required"
        hasErrors = true
      } else if (level.code.length > 50) {
        levelErrors.code = "Level code must be 50 characters or less"
        hasErrors = true
      }

      if (level.promotion_timeline && level.promotion_timeline.length > 50) {
        levelErrors.promotion_timeline = "Promotion timeline must be 50 characters or less"
        hasErrors = true
      }

      if (Object.keys(levelErrors).length > 0) {
        newErrors[index] = levelErrors
      }
    })

    const codes = levels.map((l) => l.code.trim()).filter(Boolean)
    const duplicateCodes = codes.filter((code, index) => codes.indexOf(code) !== index)

    if (duplicateCodes.length > 0) {
      levels.forEach((level, index) => {
        if (duplicateCodes.includes(level.code.trim())) {
          if (!newErrors[index]) newErrors[index] = {}
          newErrors[index].code = "Duplicate level code"
          hasErrors = true
        }
      })
    }

    setErrors(newErrors)
    return !hasErrors
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validateForm()) return

    try {
      setIsSubmitting(true)
      setError(null)

      console.log("Creating levels:", levels)

      for (const level of levels) {
        console.log("Creating level:", level)
        await createLevel(level)
      }

      console.log("All levels created successfully")

      setLevels([{ code: "", order: 1, promotion_timeline: "" }])
      setErrors({})
      fetchExistingLevels()
      onSuccess?.()
      toast.success(`${levels.length} level${levels.length > 1 ? "s" : ""} created successfully`)
    } catch (err: any) {
      console.error("Error creating levels:", err)

      let errorMessage = "Failed to create levels"

      if (err?.response?.data?.error) {
        errorMessage = err.response.data.error
      } else if (err?.response?.data?.message) {
        errorMessage = err.response.data.message
      } else if (err?.message) {
        errorMessage = err.message
      }

      setError(errorMessage)
      onError?.(errorMessage)
      toast.error(errorMessage)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleInputChange = (index: number, field: keyof LevelData, value: string | number) => {
    setLevels((prev) => prev.map((level, i) => (i === index ? { ...level, [field]: value } : level)))

    if (errors[index]?.[field]) {
      setErrors((prev) => ({
        ...prev,
        [index]: { ...prev[index], [field]: undefined },
      }))
    }

    if (error) {
      setError(null)
    }
  }

  const addLevel = () => {
    const lastLevel = levels[levels.length - 1]
    setLevels((prev) => [
      ...prev,
      {
        code: "",
        order: lastLevel.order + 1,
        promotion_timeline: lastLevel.promotion_timeline,
      },
    ])
  }

  const removeLevel = (index: number) => {
    if (levels.length > 1) {
      setLevels((prev) => prev.filter((_, i) => i !== index))
      setErrors((prev) => {
        const newErrors = { ...prev }
        delete newErrors[index]
        const reindexedErrors: { [key: number]: Partial<LevelData> } = {}
        Object.keys(newErrors).forEach((key) => {
          const oldIndex = Number.parseInt(key)
          if (oldIndex > index) {
            reindexedErrors[oldIndex - 1] = newErrors[oldIndex]
          } else if (oldIndex < index) {
            reindexedErrors[oldIndex] = newErrors[oldIndex]
          }
        })
        return reindexedErrors
      })
    }
  }

  const duplicateLevel = (index: number) => {
    const levelToDuplicate = levels[index]
    setLevels((prev) => [
      ...prev.slice(0, index + 1),
      {
        ...levelToDuplicate,
        code: "",
        order: levelToDuplicate.order + 1,
      },
      ...prev.slice(index + 1),
    ])
  }

  const handleClearAll = () => {
    setLevels([{ code: "", order: 1, promotion_timeline: "" }])
    setErrors({})
    setError(null)
  }

  const commonTimelineOptions = ["3-6 months", "6-12 months", "12-18 months", "18-24 months", "2-3 years"]

  return (
    <div className="container mt-5">
      <div>
        <ErrorBox errorMessage={error} />
      <div className="flex items-center justify-end mb-4">
        {permissions.includes("organization_structure.add_department") && (
          <button
          onClick={() => navigate("/organization-structure/levels/departments")}
          className="flex items-center px-4 py-2 bg-brand-500 hover:bg-brand-600 text-white text-md font-medium rounded-lg transition-colors duration-200"
          >
          <i className="fas fa-plus mr-2"></i>
          Add Department
          </button>
        )}
      </div>
        <div className="bg-white dark:bg-navy-800 rounded-lg shadow-sm border border-gray-200 dark:border-white/10">
          <div className="p-6">
            <div className="mb-8">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-brand-500 dark:bg-brand-600 rounded-lg flex items-center justify-center">
                    <Building2 className="h-4 w-4 text-white" />
                  </div>
                  <h3 className="text-lg font-semibold text-navy-700 dark:text-white">Existing Levels</h3>
                </div>
                <button
                  type="button"
                  onClick={() => setShowExistingLevels(!showExistingLevels)}
                  className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors"
                >
                  {showExistingLevels ? (
                    <>
                      <EyeOff className="h-4 w-4" />
                      Hide
                    </>
                  ) : (
                    <>
                      <Eye className="h-4 w-4" />
                      Show
                    </>
                  )}
                </button>
              </div>

              {showExistingLevels && (
                <div className="bg-gray-50 dark:bg-navy-700/30 rounded-lg border border-gray-200 dark:border-white/10 p-4">
                  {loadingExisting ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
                      <span className="ml-2 text-sm text-gray-500 dark:text-gray-400">Loading existing levels...</span>
                    </div>
                  ) : existingLevels.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                      {existingLevels
                        .sort((a, b) => a.order - b.order)
                        .map((level) => (
                          <div
                            key={level.id}
                            className="bg-white dark:bg-navy-600 rounded-lg border border-gray-200 dark:border-white/10 p-4 hover:shadow-sm transition-shadow"
                          >
                            <div className="flex items-center justify-between mb-2">
                              <Badge
                                variant="secondary"
                                className="bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300"
                              >
                                {level.code}
                              </Badge>
                              <span className="text-xs text-gray-500 dark:text-gray-400">Order: {level.order}</span>
                            </div>
                            {level.promotion_timeline && (
                              <p className="text-sm text-gray-600 dark:text-gray-300 mb-1">
                                <span className="font-medium">Timeline:</span> {level.promotion_timeline}
                              </p>
                            )}
                          </div>
                        ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <Building2 className="h-12 w-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
                      <p className="text-sm text-gray-500 dark:text-gray-400">No levels created yet</p>
                      <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">Create your first level below</p>
                    </div>
                  )}
                </div>
              )}
            </div>

            <form onSubmit={handleSubmit} className="space-y-8">
              <div>
                <div className="mb-6">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 bg-brand-500 dark:bg-brand-600 rounded-lg flex items-center justify-center">
                      <Building2 className="h-5 w-5 text-white" />
                    </div>
                    <h2 className="text-xl font-bold text-navy-700 dark:text-white">Create Organization Levels</h2>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Add multiple organizational levels at once with promotion timeline information
                  </p>
                </div>
              </div>

              <div>
                <div className="mb-6">
                  <h3 className="text-lg font-bold text-navy-700 dark:text-white mb-2">Level Configuration</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Configure each level with a unique code, order position, and optional promotion timeline
                  </p>
                </div>

                <div className="space-y-6">
                  {levels.map((level, index) => (
                    <div
                      key={index}
                      className="bg-gray-50 dark:bg-navy-700/50 rounded-lg border border-gray-200 dark:border-white/10 p-6"
                    >
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-2">
                          <Badge
                            variant="primary"
                            className="bg-brand-100 dark:bg-brand-900/30 text-brand-700 dark:text-brand-300"
                          >
                            Level {index + 1}
                          </Badge>
                        </div>
                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={() => duplicateLevel(index)}
                            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-200 dark:hover:bg-navy-600 rounded-lg transition-colors"
                            title="Duplicate level"
                          >
                            <Copy className="h-4 w-4" />
                          </button>
                          {levels.length > 1 && (
                            <button
                              type="button"
                              onClick={() => removeLevel(index)}
                              className="p-2 text-red-400 hover:text-red-600 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                              title="Remove level"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div>
                          <InputField
                            id={`code-${index}`}
                            label="Level Code"
                            extra="w-full"
                            placeholder="e.g., L1, L2, Senior"
                            variant="auth"
                            state={errors[index]?.code ? "error" : undefined}
                            value={level.code}
                            onChange={(e) => handleInputChange(index, "code", e.target.value)}
                            maxLength={50}
                          />
                          {errors[index]?.code && (
                            <p className="mt-1 text-sm text-red-500 dark:text-red-400">{errors[index].code}</p>
                          )}
                        </div>

                        <div>
                          <InputField
                            id={`order-${index}`}
                            label="Order Position"
                            extra="w-full"
                            placeholder="1"
                            variant="auth"
                            state={errors[index]?.order ? "error" : undefined}
                            type="number"
                            min="1"
                            value={level.order.toString()}
                            onChange={(e) => handleInputChange(index, "order", Number.parseInt(e.target.value) || 1)}
                          />
                          {errors[index]?.order && (
                            <p className="mt-1 text-sm text-red-500 dark:text-red-400">{errors[index].order}</p>
                          )}
                        </div>

                        <div>
                          <div className="flex items-center gap-2 mb-2">
                            <InputField
                              id={`timeline-${index}`}
                              label="Promotion Timeline (Optional)"
                              extra="w-full"
                              placeholder="e.g., 6-12 months"
                              variant="auth"
                              state={errors[index]?.promotion_timeline ? "error" : undefined}
                              value={level.promotion_timeline}
                              onChange={(e) => handleInputChange(index, "promotion_timeline", e.target.value)}
                              maxLength={50}
                            />
                          </div>
                          {errors[index]?.promotion_timeline && (
                            <p className="mt-1 text-sm text-red-500 dark:text-red-400">
                              {errors[index].promotion_timeline}
                            </p>
                          )}
                        </div>
                      </div>

                      <div className="mt-4">
                        <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Quick select:</p>
                        <div className="flex flex-wrap gap-2">
                          {commonTimelineOptions.map((option) => (
                            <button
                              key={option}
                              type="button"
                              onClick={() => handleInputChange(index, "promotion_timeline", option)}
                              className="px-3 py-1 text-xs bg-white dark:bg-navy-600 border border-gray-300 dark:border-white/10 rounded-md hover:bg-gray-50 dark:hover:bg-navy-500 text-navy-700 dark:text-gray-300 transition-colors"
                            >
                              {option}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}

                  <div className="flex justify-center pt-4">
                    <button
                      type="button"
                      onClick={addLevel}
                      className="flex items-center gap-2 px-4 py-2 text-white  bg-brand-500 dark:bg-brand-600 border border-brand-200 dark:border-brand-800 rounded-lg hover:bg-brand-600 dark:hover:bg-brand-900/30 transition-colors"
                    >
                      <Plus className="h-4 w-4" />
                      Add Another Level
                    </button>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-end space-x-4 pt-6 border-t border-gray-200 dark:border-white/10">
                <Button type="button" varient="outline" onClick={handleClearAll} disabled={isSubmitting}>
                  Clear All
                </Button>
                <Button type="submit" varient="primary" disabled={isSubmitting} className="flex items-center">
                  {isSubmitting ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      Creating {levels.length} Level{levels.length > 1 ? "s" : ""}...
                    </>
                  ) : (
                    <>
                      <Building2 className="h-4 w-4 mr-2" />
                      Create {levels.length} Level{levels.length > 1 ? "s" : ""}
                    </>
                  )}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  )
}
